import React, { useEffect, lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { getUserProfile, restoreSession, clearError } from './store/slices/authSlice';
import { clearMilestone } from './store/slices/auraSlice';
import { formatUserFacingError } from './utils/formatUserFacingError';
import { buildLoginPath, buildRegisterPath, locationToPath } from './utils/authRedirect';

// Components
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import LoadingSpinner from './components/common/LoadingSpinner';
import SearchBar from './components/common/SearchBar';
import SoftErrorBoundary from './components/common/SoftErrorBoundary';
import { ToastManager } from './components/common/Toast';
import AuraMilestoneCelebration from './components/aura/AuraMilestoneCelebration';
import LocationPermissionEducationModal from './components/location/LocationPermissionEducationModal';
import OnboardingOrchestrator from './components/onboarding/OnboardingOrchestrator';
import SellerSaleNotificationsBridge from './components/notifications/SellerSaleNotificationsBridge';
import GiftCardNotificationManager from './components/giftcards/GiftCardNotificationManager';

// Pages
import SearchResultsPage from './pages/search/SearchResultsPage';
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import PasswordResetPage from './pages/auth/PasswordResetPage';
import ItemsPage from './pages/items/ItemsPage';
import HobbiesFindsPage from './pages/items/HobbiesFindsPage';
import ItemDetailPage from './pages/items/ItemDetailPage';
import ListingDeepLinkPage from './pages/items/ListingDeepLinkPage';
import CreateItemPage from './pages/items/CreateItemPage';
import EditItemPage from './pages/items/EditItemPage';
import FavoritesPage from './pages/items/FavoritesPage';
import MyItemsPage from './pages/items/MyItemsPage';
import WhatToOfferPage from './pages/items/WhatToOfferPage';
import DesignersPage from './pages/designers/DesignersPage';
import ArtistsPage from './pages/artists/ArtistsPage';
import ArtistItemDetailPage from './pages/artists/ArtistItemDetailPage';
import OfferDesignerItemPage from './pages/designers/OfferDesignerItemPage';
import DesignerItemDetailPage from './pages/designers/DesignerItemDetailPage';
import CreateShopPage from './pages/designers/CreateShopPage';
import ShopDetailPage from './pages/designers/ShopDetailPage';
import ShopPendingPage from './pages/shops/ShopPendingPage';
import EditShopPage from './pages/designers/EditShopPage';
import BoutiquesPage from './pages/boutiques/BoutiquesPage';
import OfferBoutiqueItemPage from './pages/boutiques/OfferBoutiqueItemPage';
import BoutiqueItemDetailPage from './pages/boutiques/BoutiqueItemDetailPage';
import CreateBoutiquePage from './pages/boutiques/CreateBoutiquePage';
import BoutiqueDetailPage from './pages/boutiques/BoutiqueDetailPage';
import EditBoutiquePage from './pages/boutiques/EditBoutiquePage';
import WeddingEssentialsPage from './pages/wedding/WeddingEssentialsPage';
import CreateWeddingPreLovedItemPage, {
  EditWeddingPreLovedItemPage,
} from './pages/wedding/CreateWeddingPreLovedItemPage';
import ProvideWeddingServicePage from './pages/wedding/ProvideWeddingServicePage';
import LuxuryPage from './pages/luxury/LuxuryPage';
import ListLuxuryPiecePage from './pages/luxury/ListLuxuryPiecePage';
import CartPage from './pages/cart/CartPage';
import CheckoutPage from './pages/checkout/CheckoutPage';
import InstapayInstructionsPage from './pages/checkout/InstapayInstructionsPage';
import OrdersPage from './pages/orders/OrdersPage';
import OrderDetailPage from './pages/orders/OrderDetailPage';
import ProfilePage from './pages/profile/ProfilePage';
import PublicProfilePage from './pages/profile/PublicProfilePage';
import PointsPage from './pages/points/PointsPage';
import GiftCardsPage from './pages/giftcards/GiftCardsPage';
import NegotiationsPage from './pages/negotiations/NegotiationsPage';
import NegotiationDetailPage from './pages/negotiations/NegotiationDetailPage';
import ArtistCustomizationInboxPage from './pages/customizations/ArtistCustomizationInboxPage';
import ArtistCustomizationDetailPage from './pages/customizations/ArtistCustomizationDetailPage';
import ArtistCustomizationQuotePage from './pages/customizations/ArtistCustomizationQuotePage';
import ArtistCustomizationSettingsPage from './pages/customizations/ArtistCustomizationSettingsPage';
import BoutiqueCustomizationInboxPage, {
  DesignerCustomizationInboxPage,
} from './pages/customizations/BoutiqueCustomizationInboxPage';
import BoutiqueCustomizationDetailPage, {
  DesignerCustomizationDetailPage,
} from './pages/customizations/BoutiqueCustomizationDetailPage';
import BoutiqueCustomizationQuotePage, {
  DesignerCustomizationQuotePage,
} from './pages/customizations/BoutiqueCustomizationQuotePage';
import BoutiqueCustomizationSettingsPage, {
  DesignerCustomizationSettingsPage,
} from './pages/customizations/BoutiqueCustomizationSettingsPage';
import {
  BuyerCustomizationDetailPage,
  BuyerCustomizationIntroPage,
  BuyerCustomizationRecommendationsPage,
  BuyerCustomizationRequestsPage,
  BuyerCustomizationSendPage,
  BuyerCustomizationWizardPage,
} from './pages/customizations/BuyerCustomizationPages';
import {
  BuyerBoutiqueIntroPage,
  BuyerBoutiqueOfferingsPage,
  BuyerBoutiquePickerPage,
  BuyerBoutiqueWizardPage,
  BuyerDesignerIntroPage,
  BuyerDesignerOfferingsPage,
  BuyerDesignerPickerPage,
  BuyerDesignerWizardPage,
} from './pages/customizations/BuyerBoutiqueCustomizationPages';
import MyInterestsPage from './pages/interests/MyInterestsPage';
import CourierPage from './pages/courier/CourierPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import DataDeletionPage from './pages/DataDeletionPage';
import FAQPage from './pages/FAQPage';
import SupportPage from './pages/SupportPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import SeoLandingPage from './pages/seo/SeoLandingPage';

// Admin UI + adminAPI/itemsAdminAPI only download when an allowed admin opens /admin.
const HomePage = lazy(() => import('./pages/HomePage'));
const AdminDashboardPage = lazy(() => import('./pages/admin/AdminDashboardPage'));

// Protected Route Component — prefer Create Account, preserve return path
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, user, sessionChecked } = useSelector((state) => state.auth);
  const location = useLocation();

  if (!sessionChecked || (loading && !user)) {
    return <LoadingSpinner />;
  }

  if (isAuthenticated) return children;

  const returnTo = locationToPath(location) || '/';
  return (
    <Navigate
      to={buildRegisterPath(returnTo, { requireAuth: true })}
      state={{ from: returnTo, requireAuth: true }}
      replace
    />
  );
};

// Admin-only route: requires authentication + admin role (server-validated)
const ADMIN_WHITELIST = ['flea.eg@gmail.com'];
const AdminRoute = ({ children }) => {
  const { isAuthenticated, loading, user, sessionChecked } = useSelector((state) => state.auth);
  const location = useLocation();

  if (!sessionChecked || (loading && !user)) {
    return <LoadingSpinner />;
  }
  if (!isAuthenticated) {
    const returnTo = locationToPath(location) || '/admin';
    return (
      <Navigate
        to={buildLoginPath(returnTo)}
        state={{ from: returnTo }}
        replace
      />
    );
  }
  const isAdmin = user?.email && ADMIN_WHITELIST.includes(user.email.toLowerCase());
  if (!isAdmin) {
    return <Navigate to="/" />;
  }
  return children;
};

function App() {
  const dispatch = useDispatch();
  const { isAuthenticated, error, user, sessionChecked } = useSelector((state) => state.auth);
  const { milestoneReached } = useSelector((state) => state.aura);

  // Rehydrate auth from httpOnly cookies after hard refresh (Redux state is empty).
  useEffect(() => {
    if (!sessionChecked) {
      dispatch(restoreSession());
    }
  }, [dispatch, sessionChecked]);

  // Fetch full profile when authenticated but user data is incomplete (e.g. after signup).
  useEffect(() => {
    if (sessionChecked && isAuthenticated && (!user || !user.username)) {
      dispatch(getUserProfile()).catch(() => {});
    }
  }, [dispatch, sessionChecked, isAuthenticated, user]);

  useEffect(() => {
    if (!error) return undefined;
    toast.error(formatUserFacingError(error, 'Something went wrong'));
    dispatch(clearError());
    return undefined;
  }, [dispatch, error]);

  const handleCloseCelebration = () => {
    dispatch(clearMilestone());
  };

  const location = useLocation();
  const isHomePage = location.pathname === '/';

  return (
    <div className="App">
      <ToastManager />
      <SoftErrorBoundary name="seller-notifications">
        {isAuthenticated ? <SellerSaleNotificationsBridge /> : null}
      </SoftErrorBoundary>
      <SoftErrorBoundary name="gift-notifications">
        {isAuthenticated ? <GiftCardNotificationManager /> : null}
      </SoftErrorBoundary>
      <SoftErrorBoundary name="aura-milestone">
        {milestoneReached && (
          <AuraMilestoneCelebration
            milestoneValue={milestoneReached.milestoneValue}
            pointsAwarded={milestoneReached.pointsAwarded}
            onClose={handleCloseCelebration}
          />
        )}
      </SoftErrorBoundary>
      <SoftErrorBoundary name="location-permission-modal">
        <LocationPermissionEducationModal />
      </SoftErrorBoundary>
      <SoftErrorBoundary name="onboarding-orchestrator">
        <OnboardingOrchestrator />
      </SoftErrorBoundary>
      <SoftErrorBoundary name="header">
        <Header />
      </SoftErrorBoundary>
      <SoftErrorBoundary name="searchbar">
        {!isHomePage && <SearchBar />}
      </SoftErrorBoundary>
      <main className="min-h-screen">
        <Routes>
          {/* Public Routes */}
          <Route
            path="/"
            element={
              <Suspense fallback={<LoadingSpinner />}>
                <HomePage />
              </Suspense>
            }
          />
          <Route path="/search" element={<SearchResultsPage />} />
          <Route path="/second-hand-egypt" element={<SeoLandingPage slug="second-hand-egypt" />} />
          <Route path="/thrift-egypt" element={<SeoLandingPage slug="thrift-egypt" />} />
          <Route path="/flea-market-egypt" element={<SeoLandingPage slug="flea-market-egypt" />} />
          <Route path="/ar/second-hand-egypt" element={<SeoLandingPage slug="second-hand-egypt" arabic />} />
          <Route path="/ar/thrift-egypt" element={<SeoLandingPage slug="thrift-egypt" arabic />} />
          <Route path="/ar/flea-market-egypt" element={<SeoLandingPage slug="flea-market-egypt" arabic />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/reset-password" element={<PasswordResetPage />} />
          <Route path="/reset-password/:uid/:token" element={<PasswordResetPage />} />
          <Route path="/buy/:categorySlug" element={<ItemsPage />} />
          <Route path="/pre-owned-items" element={<ItemsPage />} />
          <Route path="/items" element={<ItemsPage />} />
          <Route path="/hobbies-finds" element={<HobbiesFindsPage />} />
          <Route path="/defected-items" element={<Navigate to="/pre-owned-items" replace />} />
          <Route path="/listing/:itemRef" element={<ListingDeepLinkPage />} />
          <Route path="/pre-owned-items/:categorySlug/:itemSlug" element={<ItemDetailPage />} />
          <Route path="/pre-owned-items/:itemRef" element={<ItemDetailPage />} />
          <Route path="/items/:categorySlug/:itemSlug" element={<ItemDetailPage />} />
          <Route path="/items/:itemRef" element={<ItemDetailPage />} />
          <Route path="/what-to-offer" element={<WhatToOfferPage />} />
          <Route path="/designers" element={<DesignersPage />} />
          <Route path="/boutiques" element={<BoutiquesPage />} />
          <Route path="/artists" element={<ArtistsPage />} />
          <Route path="/artist-items/:itemRef" element={<ArtistItemDetailPage />} />
          <Route path="/wedding-essentials" element={<WeddingEssentialsPage />} />
          <Route
            path="/provide-wedding-service"
            element={
              <ProtectedRoute>
                <ProvideWeddingServicePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/create-wedding-pre-loved-item"
            element={
              <ProtectedRoute>
                <CreateWeddingPreLovedItemPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/edit-wedding-pre-loved-item/:itemRef"
            element={
              <ProtectedRoute>
                <EditWeddingPreLovedItemPage />
              </ProtectedRoute>
            }
          />
          <Route path="/luxury" element={<LuxuryPage />} />
          <Route path="/luxury-items" element={<Navigate to="/luxury" replace />} />
          <Route
            path="/list-luxury-piece"
            element={
              <ProtectedRoute>
                <ListLuxuryPiecePage />
              </ProtectedRoute>
            }
          />
          <Route path="/local-shops" element={<Navigate to="/boutiques" replace />} />
          <Route path="/boutique-items/:itemRef" element={<BoutiqueItemDetailPage />} />
          <Route path="/designer-items/:itemRef" element={<DesignerItemDetailPage />} />
          <Route path="/designer-shop/:shopRef" element={<ShopDetailPage />} />
          <Route path="/artist-shop/:shopRef" element={<ShopDetailPage />} />
          <Route path="/boutique-shop/:shopRef" element={<BoutiqueDetailPage />} />
          <Route
            path="/shop-pending"
            element={
              <ProtectedRoute>
                <ShopPendingPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/create-shop"
            element={
              <ProtectedRoute>
                <CreateShopPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/designer-shop/edit"
            element={
              <ProtectedRoute>
                <EditShopPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/offer-designer-item"
            element={
              <ProtectedRoute>
                <OfferDesignerItemPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/offer-artist-item"
            element={
              <ProtectedRoute>
                <OfferDesignerItemPage vertical="artist" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/create-boutique"
            element={
              <ProtectedRoute>
                <CreateBoutiquePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/boutique-shop/edit"
            element={
              <ProtectedRoute>
                <EditBoutiquePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/offer-boutique-item"
            element={
              <ProtectedRoute>
                <OfferBoutiqueItemPage />
              </ProtectedRoute>
            }
          />
          <Route path="/favorites" element={<FavoritesPage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/data-deletion" element={<DataDeletionPage />} />
          <Route path="/faq" element={<FAQPage />} />
          <Route path="/support" element={<SupportPage />} />
          <Route path="/terms" element={<TermsOfServicePage />} />
          <Route path="/u/:username" element={<PublicProfilePage />} />
          
          {/* Protected Routes */}
          <Route
            path="/create-item"
            element={
              <ProtectedRoute>
                <CreateItemPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/edit-item/:itemRef"
            element={
              <ProtectedRoute>
                <EditItemPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/cart"
            element={
              <ProtectedRoute>
                <CartPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/checkout"
            element={
              <ProtectedRoute>
                <CheckoutPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/checkout/instapay"
            element={
              <ProtectedRoute>
                <InstapayInstructionsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/orders"
            element={
              <ProtectedRoute>
                <OrdersPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/orders/:orderRef"
            element={
              <ProtectedRoute>
                <OrderDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/my-catalogue"
            element={
              <ProtectedRoute>
                <MyItemsPage />
              </ProtectedRoute>
            }
          />
          <Route path="/my-items" element={<Navigate to="/my-catalogue" replace />} />
          <Route
            path="/interests"
            element={
              <ProtectedRoute>
                <MyInterestsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <ProfilePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/wallet-balance-and-aura"
            element={
              <ProtectedRoute>
                <PointsPage />
              </ProtectedRoute>
            }
          />
          <Route path="/points" element={<Navigate to="/wallet-balance-and-aura" replace />} />
          <Route
            path="/giftcards"
            element={
              <ProtectedRoute>
                <GiftCardsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/negotiations"
            element={
              <ProtectedRoute>
                <NegotiationsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/negotiations/:negotiationRef"
            element={
              <ProtectedRoute>
                <NegotiationDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/artist/customization-requests"
            element={
              <ProtectedRoute>
                <ArtistCustomizationInboxPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/artist/customization-requests/:requestId"
            element={
              <ProtectedRoute>
                <ArtistCustomizationDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/artist/customization-requests/:requestId/quote"
            element={
              <ProtectedRoute>
                <ArtistCustomizationQuotePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/artist/customization-settings"
            element={
              <ProtectedRoute>
                <ArtistCustomizationSettingsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/boutique/customization-requests"
            element={
              <ProtectedRoute>
                <BoutiqueCustomizationInboxPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/boutique/customization-requests/:requestId"
            element={
              <ProtectedRoute>
                <BoutiqueCustomizationDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/boutique/customization-requests/:requestId/quote"
            element={
              <ProtectedRoute>
                <BoutiqueCustomizationQuotePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/boutique/customization-settings"
            element={
              <ProtectedRoute>
                <BoutiqueCustomizationSettingsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/designer/customization-requests"
            element={
              <ProtectedRoute>
                <DesignerCustomizationInboxPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/designer/customization-requests/:requestId"
            element={
              <ProtectedRoute>
                <DesignerCustomizationDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/designer/customization-requests/:requestId/quote"
            element={
              <ProtectedRoute>
                <DesignerCustomizationQuotePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/designer/customization-settings"
            element={
              <ProtectedRoute>
                <DesignerCustomizationSettingsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/custom-order"
            element={<ProtectedRoute><BuyerBoutiqueIntroPage /></ProtectedRoute>}
          />
          <Route
            path="/custom-order/boutiques"
            element={<ProtectedRoute><BuyerBoutiquePickerPage /></ProtectedRoute>}
          />
          <Route
            path="/custom-order/boutiques/:shopId"
            element={<ProtectedRoute><BuyerBoutiqueOfferingsPage /></ProtectedRoute>}
          />
          <Route
            path="/custom-order/:requestId/step/:step"
            element={<ProtectedRoute><BuyerBoutiqueWizardPage /></ProtectedRoute>}
          />
          <Route
            path="/custom-order/designers/start"
            element={<ProtectedRoute><BuyerDesignerIntroPage /></ProtectedRoute>}
          />
          <Route
            path="/custom-order/designers"
            element={<ProtectedRoute><BuyerDesignerPickerPage /></ProtectedRoute>}
          />
          <Route
            path="/custom-order/designers/:shopId"
            element={<ProtectedRoute><BuyerDesignerOfferingsPage /></ProtectedRoute>}
          />
          <Route
            path="/custom-order/designer/:requestId/step/:step"
            element={<ProtectedRoute><BuyerDesignerWizardPage /></ProtectedRoute>}
          />
          <Route
            path="/custom-painting"
            element={<ProtectedRoute><BuyerCustomizationIntroPage /></ProtectedRoute>}
          />
          <Route
            path="/custom-painting/:requestId/step/:step"
            element={<ProtectedRoute><BuyerCustomizationWizardPage /></ProtectedRoute>}
          />
          <Route
            path="/custom-painting/:requestId/artists"
            element={<ProtectedRoute><BuyerCustomizationRecommendationsPage /></ProtectedRoute>}
          />
          <Route
            path="/custom-painting/:requestId/send/:selectedShopId"
            element={<ProtectedRoute><BuyerCustomizationSendPage /></ProtectedRoute>}
          />
          <Route
            path="/customization-requests"
            element={<ProtectedRoute><BuyerCustomizationRequestsPage /></ProtectedRoute>}
          />
          <Route
            path="/customization-requests/:requestId"
            element={<ProtectedRoute><BuyerCustomizationDetailPage /></ProtectedRoute>}
          />
          <Route
            path="/courier"
            element={
              <ProtectedRoute>
                <CourierPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <AdminRoute>
                <Suspense fallback={<LoadingSpinner />}>
                  <AdminDashboardPage />
                </Suspense>
              </AdminRoute>
            }
          />
        </Routes>
      </main>
      <Footer />
    </div>
  );
}

export default App;
